import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

// 列表格式
interface DataType {
  // 标题名称
  name?: string;
  // 内容列表
  list?: ListType[];
}
// 内容格式
interface ListType {
  title?: any;
  id?: any;
}
// 触发方式
type Trigger = 'click' | 'hover';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'icc-order',
  styleUrls: [`./icc-order.component.less`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="content" #content>
      <div
        class="order-wrapper"
        [ngClass]="{ active: menuState }"
        [ngStyle]="{ right: position[0] + 'px', bottom: position[1] + 'px' }"
      >
        <div class="order-content">
          <div class="title">
            {{ title }}
          </div>
          <div class="scroll" [ngStyle]="{ maxWidth: width + 'px', maxHeight: height + 'px' }">
            <ng-container *ngIf="data && data.length > 0">
              <ul>
                <li *ngFor="let item of data; index as level">
                  <div class="name">
                    {{ item.name }}
                  </div>
                  <div
                    class="item"
                    *ngFor="let it of item.list; index as i"
                    [ngClass]="{ active: level === currentLevel && i === currentIndex }"
                  >
                    <p class="des" (click)="markIndex(level, i)">{{ it.title }}</p>
                    <p class="btn">
                      <a (click)="move(level, i, false)" [hidden]="level === currentLevel && currentIndex <= 0">
                        <i nz-icon nzType="arrow-up" nzTheme="outline"></i>
                      </a>
                      <a
                        (click)="move(level, i, true)"
                        [hidden]="level === currentLevel && currentIndex + 1 >= item.list.length"
                      >
                        <i nz-icon nzType="arrow-down" nzTheme="outline"></i>
                      </a>
                    </p>
                  </div>
                </li>
              </ul>
              <span class="tip" [ngStyle]="{ top: tipTop + 'px' }"> </span>
            </ng-container>

            <!-- 缺省 -->
            <ng-container *ngIf="data && data.length < 1">
              <div class="empty-box">
                <img src="./assets/images/nothing/search.png" alt="" />
                <p class="empty-text">暂无数据</p>
              </div>
            </ng-container>
          </div>
          <div class="footer" *ngIf="data && data.length > 0">
            <icc-button (click)="saveHandle()">保存</icc-button>
            <icc-button btnType="hollow" (click)="freshHandle()">重置</icc-button>
          </div>
        </div>
      </div>
      <div class="trigger" (click)="toggleMenu()" [ngStyle]="{ cursor: trigger === 'hover' ? 'normal' : 'pointer' }">
        <img src="assets/images/layout/anchor.png" />
      </div>
    </div>
    <div *ngIf="menuState" class="mask" (click)="menuState = false"></div>
  `,
})
export class IccOrderComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private cdr: ChangeDetectorRef) {}
  // 标题高度
  private nameHeight = 35;
  // 单行内容高度
  private itemHeight = 30;

  /**
   * 菜单状态
   */
  menuState = false;
  /**
   * 当前层级
   */
  currentLevel = 0;
  /**
   * 当前层级的索引
   */
  currentIndex = null;
  /**
   * 指示符的top
   */
  tipTop = 0;
  /**
   * 鼠标移动流
   */
  mouseMove$: Subscription;
  /**
   * 鼠标离开流
   */
  mouseLeave$: Subscription;
  /**
   * 大标题
   */
  @Input() title = '已选数据排序';
  /**
   * 组件最大宽度
   */
  @Input() width = 340;
  /**
   * 组件最大高度(溢出部分滚动条处理)
   */
  @Input() height = 600;
  /**
   * 列表
   */
  @Input() data: DataType[] = [];
  /**
   * 触发方式
   * - click 单机
   * - hover 鼠标移动（默认）
   */
  @Input() trigger: Trigger = 'hover';
  /**
   * 组件的位置
   * - [right,bottom] 数字
   */
  @Input() position = [30, 0];
  /**
   * 保存的回调
   */
  @Output() save = new EventEmitter<DataType[]>();
  /**
   * 重置的回调
   */
  @Output() fresh = new EventEmitter<any>();

  @ViewChild('content', { static: false }) content;

  /**
   *  获取有效区域内元素的总数量
   */
  getTotalItemCount() {
    let result = 0;
    this.data.map((v, i) => {
      if (i >= this.currentLevel) return;
      result += v.list.length;
    });
    return result;
  }
  /**
   *  改变指示符的top值
   */
  changeTipTop() {
    const itemTotalHeight = (this.getTotalItemCount() + this.currentIndex) * this.itemHeight;
    const nameTotalHeight = (this.currentLevel + 1) * this.nameHeight;
    this.tipTop = nameTotalHeight + itemTotalHeight + 7;
  }
  /**
   * 初始化标记索引
   */
  ngOnInit() {
    this.tipTop = -20;
  }
  /**
   * 监听鼠标移动
   */
  ngAfterViewInit() {
    if (this.trigger === 'hover') {
      const contEle = this.content.nativeElement;
      const _mouseMove$ = fromEvent(contEle, 'mouseover');
      const _mouseLeave$ = fromEvent(contEle, 'mouseleave');
      this.mouseMove$ = _mouseMove$.pipe(throttleTime(200)).subscribe(e => {
        if (this.menuState) return;
        this.menuState = true;
        this.cdr.markForCheck();
      });
      this.mouseLeave$ = _mouseLeave$.pipe(throttleTime(200)).subscribe(e => {
        this.menuState = false;
        this.cdr.markForCheck();
      });
    }
  }
  /**
   * 释放流
   */
  ngOnDestroy() {
    if (this.trigger === 'hover') {
      this.mouseMove$.unsubscribe();
      this.mouseLeave$.unsubscribe();
    }
  }
  /**
   * 标记索引
   */
  markIndex(level: number, i: number) {
    this.currentLevel = level;
    this.currentIndex = i;
    this.changeTipTop();
  }
  /**
   *  移动元素（上和下，临界的元素不移动）
   * @param level 层级
   * @param i 当前层级的索引
   * @param positive 是否正序
   */
  move(level: number = 0, i: number = 0, positive: boolean = true) {
    const ref = this.data[level];
    if (ref && ref.list) {
      const list = ref.list;
      // 下
      if (positive) {
        if (i + 1 >= list.length) return;
        this.swapArray(ref.list, i, i + 1);
        this.currentIndex++;
      } else {
        // 上
        if (i <= 0) return;
        this.swapArray(ref.list, i, i - 1);
        this.currentIndex--;
      }
      this.changeTipTop();
      this.cdr.markForCheck();
    }
  }
  /**
   *  乾坤大挪移
   * @param arr 容器
   * @param index1 当前元素的索引
   * @param index2 交换的元素的索引
   */
  swapArray(arr: any[], index1: number, index2: number): any[] {
    arr[index1] = arr.splice(index2, 1, arr[index1])[0];
    return arr;
  }
  /**
   *  控制菜单
   */
  toggleMenu() {
    if (this.trigger === 'hover') return;
    this.menuState = !this.menuState;
  }
  /**
   *  保存
   */
  saveHandle() {
    this.save.next(this.data);
    this.menuState = false;
  }
  /**
   *  重置
   */
  freshHandle() {
    this.fresh.next();
  }
}
